import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute, Params} from '@angular/router';

import {TERMS_USER_AGREEMENT_URL, TERMS_PRIVACY_POLICY_URL, CGU_VERSION} from '../../app.constants';

import {Subscription} from 'rxjs';
import {dlnEmailValidator, InvitationService} from '@logbook/shared';

import {SignUp} from './sign-up.service';
import {MixpanelService} from "@logbook/core/tracking/mixpanel.service";
import {MixpanelEvent} from "@logbook/core/tracking/mixpanel-event.enum";
import {Principal} from "@logbook/core/auth/services/principal.service";
import {LoginService} from "@logbook/core/auth/services/login.service";
import {User} from "@logbook/core/shared/models/user.model";
import {first} from "rxjs/operators";

@Component({
    selector: 'dln-register',
    templateUrl: './sign-up.component.html',
    styleUrls: [
        'sign-up.scss'
    ]
})
export class SignUpComponent implements OnInit, AfterViewInit, OnDestroy {

    public error: string;
    public userForm: FormGroup;
    public success: boolean;
    public invitation: any;
    public loadingForm: boolean;
    public userAgreementUrl = TERMS_USER_AGREEMENT_URL;
    public privacyPolicyUrl = TERMS_PRIVACY_POLICY_URL;
    private cguVersion = CGU_VERSION;

    private subscription: Subscription;

    constructor(private registerService: SignUp,
                private activatedRoute: ActivatedRoute,
                private principal: Principal,
                private loginService: LoginService,
                private invitationService: InvitationService,
                private mixpanelService: MixpanelService) {
    }

    ngOnInit() {
        const {required, minLength, maxLength, requiredTrue} = Validators;
        this.userForm = new FormGroup({
            email: new FormControl(null, [
                required,
                dlnEmailValidator(),
                minLength(1),
                maxLength(150)
            ]),
            password: new FormControl(null, [
                required,
                minLength(4),
                maxLength(50)
            ]),
            cgvAccepted: new FormControl(
                null,
                [requiredTrue]
            )
        });
        this.success = false;
        this.loadingForm = false;
    }

    ngAfterViewInit() {
        this.principal.identity().then((user: User) => {
            if (user) {
                this.userForm.get("email").setValue(user.email)
            }
        });

        this.subscription = this.activatedRoute.queryParams.subscribe((params: Params) => {
            const invitationToken = params['invitation'];

            if (invitationToken) {
                this.invitationService.getInvitation(invitationToken).pipe(first()).subscribe(
                    (invitation) => {
                        this.invitation = invitation;
                        this.userForm.get('email').setValue(invitation.guest);
                        this.userForm.get('email').disable();

                        this.mixpanelService.track(MixpanelEvent.SIGN_UP_STARTED, Object.assign({fromInvitation: true},
                            this.invitation ? {host: this.invitation.host, guest: this.invitation.guest} : {}
                        ));
                    },
                    (error) => {
                        console.error(`invitation error: ${error}`);
                        this.invitation = {error};
                        this.mixpanelService.track(MixpanelEvent.SIGN_UP_STARTED, {fromInvitation: true, error});
                    }
                );
            } else {
                this.mixpanelService.track(MixpanelEvent.SIGN_UP_STARTED, {fromInvitation: false});
            }
        });
    }

    get email() {
        return this.userForm.get('email');
    }

    get password() {
        return this.userForm.get('password');
    }

    get cgvAccepted() {
        return this.userForm.get('cgvAccepted');
    }

    register() {
        if (this.userForm.invalid) {
            this.error = 'ERROR';
        } else {
            this.error = null;

            const userToregister = Object.assign(new User(), this.userForm.getRawValue(), {
                langKey: 'en',
                userId: this.email.value,
                cguVersion: this.cguVersion
            });
            delete userToregister.phone;

            const invitationToken = this.invitation && this.invitation.token ? this.invitation.token : null;

            this.loadingForm = true;
            this.registerService.save(userToregister, invitationToken).pipe(first()).subscribe((userRegistrationResponse: any) => {
                const user: User = Object.assign(new User(), userRegistrationResponse);
                this.success = true;
                this.loadingForm = false;

                this.loginService.login({
                    username: userToregister.userId,
                    password: userToregister.password,
                    rememberMe: true
                });

                this.mixpanelService.reset()
                    .then(() => this.mixpanelService.setUser(user))
                    .then(() => this.mixpanelService.alias(this.email.value))
                    .then(() => this.mixpanelService.track(MixpanelEvent.SIGN_UP_COMPLETED))
                    .catch((error) => console.warn(error));

            }, (response) => this.processError(response));
        }
    }

    private processError(response) {
        this.success = null;
        this.loadingForm = false;
        let body;
        try {
            body = response.json();
        } catch (e) {
            body = response;
        }
        if (response.status === 400 && body.message) {
            if (body.exceptionCase && body.exceptionCase.id === 'PAR.UTILISATEUR_EXISTANT') {
                this.email.setErrors(Object.assign({}, this.email.errors, {
                    userAlreadyExists: true
                }), {emitEvent: true});
            } else {
                this.error = body.message;
            }
        } else {
            this.error = 'Cannot complete registration. Please try again or contact Teamlogs to continue';
        }
    }

    // Makes sure we don't have a memory leak by destroying the
    // Subscription when our flight-report is destroyed
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

}
